import { useCallback, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { getAssetParent } from '@frameio/assets/shared';
import type {
  CollectionAsset,
  RemoveAssetsFromShareMutationResult,
  UndoRemoveAssetsFromShareMutationResult,
} from '@frameio/graphql-client/generated';
import {
  useRemoveAssetsFromShareMutation,
  useUndoRemoveAssetsFromShareMutation,
} from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import { useToastTryCatchHandler } from '@frameio/utils/hooks/useToastTryCatchHandler';
import TEXT from './generated/TEXT/useRemoveAssetsFromShare';

TEXT.DEFINE({
  /** A message to display within a toast when assets are removed from a share */
  successToast: ({
    assetCount,
    shareName,
  }: {
    assetCount: number;
    shareName: string;
  }) =>
    plural(assetCount, {
      one: `Asset removed from "${shareName}"`,
      other: `Assets removed from "${shareName}"`,
    }),
  /** A message to display within a toast when assets are restored to a share */
  undoSuccessToast: ({
    assetCount,
    shareName,
  }: {
    assetCount: number;
    shareName: string;
  }) =>
    plural(assetCount, {
      one: `Asset restored to "${shareName}"`,
      other: `Assets restored to "${shareName}"`,
    }),
  /** A message to display within a toast when an error occurs during the attempted removal of assets from a share */
  errorToast: 'Something went wrong when removing assets from share',
  /** A message to display within a toast when an error occurs during the attempted restoration of assets to a share */
  undoErrorToast: 'Something went wrong when restoring assets from share',
  /** A label to display for the undo button */
  undoLabel: 'Undo',
});

type RemoveAssetsFromShareArgs = {
  allowUndo?: boolean;
  assetIds: Array<string>;
  shareId: string;
};

export const useRemoveAssetsFromShare = () => {
  const { cache } = useApolloClient();

  const tryCatchOnRemove = useToastTryCatchHandler({ duration: 4000 });
  const currentAssets = useRef<Array<Partial<CollectionAsset>>>(undefined);
  const removedAt = useRef<string | null | undefined>(null);

  const [executeRemoveAssetsFromShareMutation] =
    useRemoveAssetsFromShareMutation();

  const [executeUndoRemoveAssetsFromShareMutation] =
    useUndoRemoveAssetsFromShareMutation();

  const undoRemoveAssetsFromShare = useCallback(
    async (shareId: string) => {
      const response = await new Promise((resolve) => {
        const undoRemove = () =>
          executeUndoRemoveAssetsFromShareMutation({
            variables: {
              input: {
                shareId,
                removedAt: removedAt.current,
              },
            },
            /**
             * Because we fetch folder and file assets separately, and they are all typed as CollectionAsset, we cannot
             * confidently update the cache appropriately based on the mutation response, so we must refetch
             */
            refetchQueries: ['GetShareCollectionAssets', 'GetShareAssets'],
          });

        tryCatchOnRemove({
          attempt: undoRemove,
          successMessage({ data }: UndoRemoveAssetsFromShareMutationResult) {
            const share = data?.undoRemoveAssetsFromShare?.share;
            const restoredAssetIds =
              data?.undoRemoveAssetsFromShare?.metadata?.restoredAssetIds;
            resolve({ share, restoredAssetIds });

            return TEXT.undoSuccessToast({
              assetCount: restoredAssetIds?.length ?? 0,
              shareName: share?.name ?? '',
            })?.toString() as string;
          },
          errorMessage(error) {
            resolve(error);
            return TEXT.undoErrorToast;
          },
        });

        return undoRemove;
      });

      return response;
    },
    [executeUndoRemoveAssetsFromShareMutation, tryCatchOnRemove]
  );

  const removeAssetsFromShare = useCallback(
    async ({
      allowUndo = true,
      assetIds,
      shareId,
    }: RemoveAssetsFromShareArgs) => {
      const assetsWithParent = assetIds.map((id) => getAssetParent(cache, id));

      const response = await new Promise((resolve) => {
        const remove = () =>
          executeRemoveAssetsFromShareMutation({
            variables: {
              input: {
                shareId,
                assetIds,
              },
            },
            update: (_, { data }) => {
              const share = data?.removeAssetsFromShare?.share;
              const removedAssetIds =
                data?.removeAssetsFromShare?.metadata?.removedAssetIds;
              removedAt.current =
                data?.removeAssetsFromShare?.metadata?.removedAt;

              // Find all version stack assets
              const versionStacks = assetsWithParent.filter(
                (asset) => asset?.parent?.__typename === 'VersionStackAsset'
              );

              /**
               * Find all version stack ids that match up with successfully removed ids so they can be removed from the
               * cache
               */
              const versionIds = versionStacks
                .filter(
                  (stack) =>
                    removedAssetIds?.filter(
                      (removedId) => removedId === stack?.id
                    )
                )
                .map((stack) => stack?.id);

              cache.modify({
                id: cache.identify({
                  __typename: share?.__typename,
                  id: shareId,
                }),
                fields: (value, { fieldName, readField }) => {
                  if (
                    (fieldName === 'collectionAssets' ||
                      fieldName === 'assets') &&
                    value.nodes
                  ) {
                    if (!removedAssetIds || removedAssetIds?.length < 1) {
                      return value;
                    }

                    currentAssets.current = value.nodes;

                    const removedIdsIncludingVersionStackIds = [
                      ...removedAssetIds,
                      ...versionIds,
                    ];

                    return {
                      ...value,
                      nodes: value.nodes.filter(
                        (node: CollectionAsset) =>
                          !removedIdsIncludingVersionStackIds?.includes(
                            readField('id', node) ?? ''
                          )
                      ),
                      totalCount:
                        value.totalCount - (removedAssetIds?.length ?? 0),
                    };
                  }

                  if (fieldName === 'aggregationInfo') {
                    return share?.aggregationInfo;
                  }

                  return value;
                },
              });
            },
          });

        tryCatchOnRemove({
          attempt: remove,
          buttonCallback: allowUndo
            ? () => {
                undoRemoveAssetsFromShare(shareId);
              }
            : undefined,
          buttonLabel: allowUndo ? TEXT.undoLabel : undefined,
          successMessage({ data }: RemoveAssetsFromShareMutationResult) {
            const share = data?.removeAssetsFromShare?.share;
            const removedAssetIds =
              data?.removeAssetsFromShare?.metadata?.removedAssetIds;
            resolve({
              share,
              removedAssetIds,
              removedAt: data?.removeAssetsFromShare?.metadata?.removedAt,
            });

            return TEXT.successToast({
              assetCount: removedAssetIds?.length ?? 0,
              shareName: share?.name ?? '',
            })?.toString() as string;
          },
          errorMessage(error) {
            resolve(error);
            return TEXT.errorToast;
          },
        });

        return remove;
      });

      return response;
    },
    [
      cache,
      executeRemoveAssetsFromShareMutation,
      tryCatchOnRemove,
      undoRemoveAssetsFromShare,
    ]
  );

  return removeAssetsFromShare;
};
